import { useState, useEffect, useRef, useCallback } from 'react';
import { FaFilter, FaSyncAlt, FaTimes, FaSearch } from 'react-icons/fa';

const SecurityAdvancedFilters = ({ 
  filters, 
  onFilterChange, 
  onReset, 
  loading = false,
  isSearching = false
}) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [localSearch, setLocalSearch] = useState(filters.securityName || '');
  const searchInputRef = useRef(null);
  const debounceTimerRef = useRef(null);

  const securityTypesDisplay = [
    { value: 'Bonds', label: 'Obligations' },
    { value: 'Shares', label: 'Actions' },
    { value: 'ETF', label: 'Autres' }
  ];

  const targetMarketsDisplay = [
    { value: 'Public', label: 'Cote' },
    { value: 'Private', label: 'Non Cote' },
    { value: 'Monetary', label: 'Monétaire' }
  ];

  // ✅ Synchroniser UNIQUEMENT depuis l'extérieur si le champ n'a pas le focus
  useEffect(() => {
    if (document.activeElement !== searchInputRef.current && 
        filters.securityName !== localSearch) {
      setLocalSearch(filters.securityName || '');
    }
  }, [filters.securityName]); // Ne pas inclure localSearch

  // ✅ Debounce de recherche
  const handleSearchChange = useCallback((value) => {
    setLocalSearch(value);
    
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    debounceTimerRef.current = setTimeout(() => {
      onFilterChange({ securityName: value });
    }, 500); // 500ms
  }, []); // Pas de dépendances

  const handleClearSearch = useCallback(() => {
    setLocalSearch('');
    
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    onFilterChange({ securityName: '' });
    
    setTimeout(() => {
      searchInputRef.current?.focus();
    }, 0);
  }, []); // Pas de dépendances

  const handleFilterUpdate = useCallback((key, value) => {
    onFilterChange({ [key]: value });
  }, []); // Pas de dépendances

  const handleReset = useCallback(() => {
    setLocalSearch(''); // ✅ Réinitialiser le champ local
    onReset();
    setTimeout(() => {
      searchInputRef.current?.focus();
    }, 0);
  }, [onReset]);

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.securityName) count++;
    if (filters.securityType) count++;
    if (filters.targetMarket) count++;
    if (filters.issuerName) count++;
    if (filters.holderType && filters.holderType !== 'All') count++;
    return count;
  };

  const activeFilters = getActiveFiltersCount();

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return (
    <div className="search-filter-container mb-3">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
        
        <div className="d-flex align-items-center gap-2">
          <FaSearch className="text-primary" size={20} />
          <h6 className="fw-bold mb-0">Titres</h6>
        </div>
        
        <div className="filter-controls d-flex flex-wrap align-items-center gap-2">
          
          {/* Recherche */}
          <div className="position-relative">
            <FaSearch className="position-absolute top-50 start-0 translate-middle-y ms-2 text-muted" size={16} />
            <input 
              ref={searchInputRef}
              type="text" 
              className="search-input ps-4" 
              placeholder="Rechercher titre..." 
              value={localSearch} 
              onChange={e => handleSearchChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && debounceTimerRef.current) {
                  clearTimeout(debounceTimerRef.current);
                  onFilterChange({ securityName: localSearch });
                }
              }}
              disabled={loading && !isSearching}
              style={{ 
                height: '38px',
                outline: 'none',
                boxShadow: 'none',
                borderColor: localSearch ? '#0d6efd' : '#dee2e6',
                width: '280px'
              }}
            />
            {localSearch && (
              <button
                className="btn btn-link position-absolute top-50 end-0 translate-middle-y me-2 p-0"
                onClick={handleClearSearch}
                type="button"
                disabled={loading}
                style={{ 
                  color: '#6c757d',
                  outline: 'none',
                  boxShadow: 'none',
                  zIndex: 10
                }}
                onMouseDown={(e) => e.preventDefault()}
              >
                <FaTimes size={14} />
              </button>
            )}
            {isSearching && (
              <div 
                className="position-absolute top-50 end-0 translate-middle-y me-2"
                style={{ right: localSearch ? '32px' : '8px' }}
              >
                <div className="spinner-border spinner-border-sm text-muted" role="status" style={{ width: '12px', height: '12px' }}>
                  <span className="visually-hidden">Recherche...</span>
                </div>
              </div>
            )}
          </div>
          
          {/* Type de titre */}
          <select 
            className="account-type-select" 
            onChange={(e) => handleFilterUpdate('securityType', e.target.value)}
            value={filters.securityType || ""}
            disabled={loading}
            style={{ 
              height: '38px',
              outline: 'none',
              width: '120px'
            }}
          >
            <option value="">Type</option>
            {securityTypesDisplay.map(type => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
          
          {/* Marché cible */}
          <select 
            className="account-type-select" 
            onChange={(e) => handleFilterUpdate('targetMarket', e.target.value)}
            value={filters.targetMarket || ""}
            disabled={loading}
            style={{ 
              height: '38px',
              outline: 'none',
              width: '110px'
            }}
          >
            <option value="">Marché</option>
            {targetMarketsDisplay.map(market => (
              <option key={market.value} value={market.value}>
                {market.label}
              </option>
            ))}
          </select>
          
          {/* Bouton filtre avancé */}
          <button 
            className="filter-button d-flex align-items-center gap-1"
            onClick={() => setShowAdvanced(!showAdvanced)}
            disabled={loading}
            style={{ 
              height: '38px', 
              padding: '0 12px',
              outline: 'none'
            }}
          >
            <FaFilter size={14} />
            <span>Avancé</span>
          </button>
          
          {/* Reset */}
          {activeFilters > 0 && (
            <button 
              className="refresh-button d-flex align-items-center gap-1"
              onClick={handleReset}
              disabled={loading}
              style={{ 
                height: '38px', 
                padding: '0 12px',
                outline: 'none'
              }}
            >
              <FaSyncAlt size={14} />
              <span>Effacer</span>
            </button>
          )}
          
          {/* Badge filtres actifs */}
          {activeFilters > 0 && (
            <span className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25 rounded-pill px-2 py-1">
              {activeFilters}
            </span>
          )}
        </div>
      </div>

      {/* Filtres avancés */}
      {showAdvanced && (
        <div className="mt-3 pt-3 border-top">
          <div className="row g-2">
            <div className="col-md-4">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Émetteur..."
                value={filters.issuerName || ''}
                onChange={(e) => handleFilterUpdate('issuerName', e.target.value)}
                disabled={loading}
                style={{ height: '32px' }}
              />
            </div>
            
            <div className="col-md-4">
              <select
                className="form-select form-select-sm"
                value={filters.holderType || 'All'}
                onChange={(e) => handleFilterUpdate('holderType', e.target.value)}
                disabled={loading}
                style={{ height: '32px' }}
              >
                <option value="All">Tous détenteurs</option>
                <option value="Nominatif">Nominatifs</option>
                <option value="Porteur">Porteurs</option>
              </select>
            </div>
            
            <div className="col-md-4">
              <div className="d-flex gap-2">
                <select
                  className="form-select form-select-sm flex-grow-1"
                  value={filters.sortBy || 'SecurityName'}
                  onChange={(e) => handleFilterUpdate('sortBy', e.target.value)}
                  disabled={loading}
                  style={{ height: '32px' }}
                >
                  <option value="SecurityName">Nom (A-Z)</option>
                  <option value="CreationDate">Date création</option>
                  <option value="LastPrice">Dernier prix</option>
                  <option value="TotalUnitsIssue">Quantité totale</option>
                  <option value="IssueDate">Date émission</option>
                </select>
                
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => handleFilterUpdate('sortDescending', !filters.sortDescending)}
                  disabled={loading}
                  style={{ 
                    height: '32px',
                    width: '40px',
                    padding: '0'
                  }}
                >
                  {filters.sortDescending ? '↓' : '↑'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filtres actifs */}
      {activeFilters > 0 && (
        <div className="mt-2 pt-2">
          <div className="d-flex align-items-center flex-wrap gap-1">
            <small className="text-muted me-2">Filtres :</small>
            {filters.securityName && (
              <span className="badge bg-light text-dark border d-flex align-items-center gap-1 px-2 py-1 rounded-pill">
                {filters.securityName}
                <button 
                  className="btn btn-link p-0 ms-1"
                  onClick={() => {
                    setLocalSearch(''); // ✅ Réinitialiser local
                    handleFilterUpdate('securityName', '');
                  }}
                  style={{ color: '#6c757d', fontSize: '0.7rem', outline: 'none' }}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <FaTimes size={10} />
                </button>
              </span>
            )}
            {/* Autres badges... (identiques) */}
          </div>
        </div>
      )}
    </div>
  );
};

export default SecurityAdvancedFilters;